import { inject, injectable } from 'inversify';
import { firstValueFrom, take } from 'rxjs';

import { ApiConfig } from './api-config';
import { LightHttp } from './light-http.service';
import { SettingsService } from './settings.service';
import { WebSocketService } from './websocket.service';

/**
 * What `…/workflows/process-complete-data/{uuid}/{wsId}` says about a job.
 *
 * `unknown` is the server's own word for "there is no terminal state in the snapshot I can read" —
 * a job still running and a job whose failure has not become visible yet are the SAME answer, so a
 * caller may never read `unknown` as "still fine".
 */
export interface ProcessCompleteVerdict {
    state: 'success' | 'error' | 'unknown';
    data: any;
}

@injectable()
export class ProcessCompleteCheckService {
    private endpointPostfix = '/workflows/process-complete-data/';

    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(WebSocketService) private wsService: WebSocketService,
        @inject(SettingsService) private settingsService: SettingsService,
    ) {

    }

    /**
     * Ask a server what actually became of a workflow.
     *
     * `preferProcessingEdge` exists for the Studio's stall watchdog (DIAG-render-panel-stall.md).
     * With `useTaskScheduling` on, the default base is the POD — which is the very host whose
     * `replay-history` reply was stale for 231 s in that incident, so asking it again is not a
     * second opinion. When the caller passes `preferProcessingEdge` and the card knows its
     * processing edge, the question goes to that edge instead; with no `domain` to aim at there is
     * nothing to prefer and the pod still answers, because a weaker source beats no source.
     *
     * Read the honest limit of this before relying on it: `getProcessCompleteData`
     * (`WorkflowsController.kt:113`) is a plain Mongo read of `workflows` + `tasks`, and EVERY
     * ironman reads with `ReadPreference.primary()` + `ReadConcern.MAJORITY` (`MongoUtils.kt:84-85`).
     * A different host is therefore a different READER of the same majority-committed snapshot, not
     * an independent view of the job. It removes pod-host-specific failure (a wedged pod, a GC
     * storm, a tracker that never registered the card) and it does NOT survive a majority
     * commit-point stall, which is the mechanism that diagnosis favoured.
     */
    async requestToSendFileCompleteDataThroughWS(
        domain: string,
        uuid: string,
        preferProcessingEdge = false,
    ): Promise<ProcessCompleteVerdict> {
        const ff = await firstValueFrom(this.settingsService.featureFlags.pipe(take(1)));
        const base = preferProcessingEdge && domain
            ? ApiConfig.suffixDomainWithHttpsEdgeEndpoint(domain)
            : ff?.useTaskScheduling
                ? `${this.apiConfig.podEndpoint}${ApiConfig.edgePathPrefix}`
                : ApiConfig.suffixDomainWithHttpsEdgeEndpoint(domain);
        const url = `${base}${this.endpointPostfix}${uuid}/${this.wsService.getId()}`;
        return new Promise<ProcessCompleteVerdict>((resolve, reject) => {
            this.http.get(url).subscribe({
                next: (data: any) => {
                    if (data && data.error === false) {
                        resolve({
                            state: 'success',
                            data: data,
                        });
                    } else if (data && data.error === true) {
                        resolve({
                            state: 'error',
                            data: data,
                        });
                    } else {
                        resolve({
                            state: 'unknown',
                            data: data,
                        });
                    }
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }
}
